import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { getTierRanking, getTierDropdownOptions } from '../../utils/tierRankingUtils';

const TierManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, free, basic, premium, vip
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showTierModal, setShowTierModal] = useState(false);
  const [newTier, setNewTier] = useState('');
  const [tierDuration, setTierDuration] = useState(30); // วัน

  // Tier configurations
  const tierConfigs = {
    customer: {
      free: { name: 'Free', price: 0, features: ['ดูรูป 1 รูป/วัน'], color: 'bg-gray-100 text-gray-800' },
      basic: { name: 'Basic', price: 100, features: ['ดูรูป 5 รูป/วัน', 'จองได้ 3 ครั้ง/เดือน'], color: 'bg-blue-100 text-blue-800' },
      silver: { name: 'Silver', price: 300, features: ['ดูรูป 15 รูป/วัน', 'จองได้ 10 ครั้ง/เดือน'], color: 'bg-gray-100 text-gray-800' },
      gold: { name: 'Gold', price: 500, features: ['ดูรูปไม่จำกัด', 'จองได้ 20 ครั้ง/เดือน'], color: 'bg-yellow-100 text-yellow-800' },
      platinum: { name: 'Platinum', price: 1000, features: ['ดูรูปไม่จำกัด', 'จองได้ไม่จำกัด', 'สิทธิพิเศษ'], color: 'bg-purple-100 text-purple-800' }
    },
    girl: {
      free: { name: 'Free', price: 0, features: ['อัพโหลดรูป 1 รูป'], color: 'bg-gray-100 text-gray-800' },
      basic: { name: 'Basic', price: 100, features: ['อัพโหลดรูป 3 รูป', 'รายได้เพิ่ม 10%'], color: 'bg-blue-100 text-blue-800' },
      premium: { name: 'Premium', price: 300, features: ['อัพโหลดรูป 6 รูป', 'รายได้เพิ่ม 20%'], color: 'bg-green-100 text-green-800' },
      vip: { name: 'VIP', price: 500, features: ['อัพโหลดรูปไม่จำกัด', 'รายได้เพิ่ม 30%', 'สิทธิพิเศษ'], color: 'bg-purple-100 text-purple-800' }
    },
    momo: {
      starter: { name: 'Starter', price: 200, features: ['จัดการทีม 3 คน', 'รายได้ 5%'], color: 'bg-gray-100 text-gray-800' },
      basic: { name: 'Basic', price: 500, features: ['จัดการทีม 10 คน', 'รายได้ 10%'], color: 'bg-blue-100 text-blue-800' },
      pro: { name: 'Pro', price: 1000, features: ['จัดการทีม 25 คน', 'รายได้ 15%'], color: 'bg-green-100 text-green-800' },
      enterprise: { name: 'Enterprise', price: 2000, features: ['จัดการทีม 50 คน', 'รายได้ 20%'], color: 'bg-purple-100 text-purple-800' },
      ultra: { name: 'Ultra Enterprise', price: 5000, features: ['จัดการทีมไม่จำกัด', 'รายได้ 25%', 'สิทธิพิเศษ'], color: 'bg-red-100 text-red-800' }
    }
  };

  // ดึงข้อมูลผู้ใช้ทั้งหมด
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('เกิดข้อผิดพลาดในการดึงข้อมูลผู้ใช้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // เปลี่ยน Tier ของผู้ใช้
  const updateUserTier = async () => {
    if (!selectedUser || !newTier) return;

    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const tierExpiryDate = new Date();
      tierExpiryDate.setDate(tierExpiryDate.getDate() + tierDuration);

      await updateDoc(userRef, { 
        tier: newTier,
        tierExpiryDate: tierExpiryDate.toISOString(),
        tierUpdatedAt: new Date().toISOString(),
        tierUpdatedBy: 'admin'
      });

      alert(`เปลี่ยน Tier ของ ${selectedUser.displayName || selectedUser.email} เป็น ${newTier} สำเร็จ!`);
      
      // รีเฟรชข้อมูล
      await fetchUsers();
      setSelectedUser(null);
      setNewTier('');
      setShowTierModal(false);
    } catch (error) {
      console.error('Error updating tier:', error);
      alert('เกิดข้อผิดพลาดในการเปลี่ยน Tier');
    }
  };

  // กรองผู้ใช้ตามการค้นหาและ Tier
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTier = filter === 'all' || user.tier === filter;
    
    return matchesSearch && matchesTier;
  });

  // สีของ Role
  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'customer': return 'bg-blue-100 text-blue-800';
      case 'girl': return 'bg-pink-100 text-pink-800';
      case 'momo': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // ไอคอนของ Role
  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return '👑';
      case 'customer': return '👤';
      case 'girl': return '👩';
      case 'momo': return '👨‍💼';
      default: return '❓';
    }
  };

  // ตรวจสอบ Tier หมดอายุ
  const isTierExpired = (tierExpiryDate) => {
    if (!tierExpiryDate) return false;
    return new Date(tierExpiryDate) < new Date();
  };

  // รูปแบบวันที่
  const formatDate = (date) => {
    if (!date) return 'ไม่ระบุ';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">จัดการ Tier ของผู้ใช้</h2>
        <button
          onClick={fetchUsers}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
        >
          🔄 รีเฟรช
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4">
        <input
          type="text"
          placeholder="ค้นหาผู้ใช้ (ชื่อ, อีเมล, UID)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">ทุก Tier</option>
          <option value="free">Free</option>
          <option value="basic">Basic</option>
          <option value="premium">Premium</option>
          <option value="vip">VIP</option>
          <option value="silver">Silver</option>
          <option value="gold">Gold</option>
          <option value="platinum">Platinum</option>
          <option value="starter">Starter</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
          <option value="ultra">Ultra Enterprise</option>
        </select>
      </div>

      {/* Tier Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-gray-600">
            {users.filter(u => u.tier === 'free' || !u.tier).length}
          </div>
          <div className="text-sm text-gray-600">Free Tier</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">
            {users.filter(u => ['basic', 'starter'].includes(u.tier)).length}
          </div>
          <div className="text-sm text-gray-600">Basic/Starter</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">
            {users.filter(u => ['premium', 'pro', 'silver'].includes(u.tier)).length}
          </div>
          <div className="text-sm text-gray-600">Premium/Pro/Silver</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-purple-600">
            {users.filter(u => ['vip', 'gold', 'platinum', 'enterprise', 'ultra'].includes(u.tier)).length}
          </div>
          <div className="text-sm text-gray-600">VIP/Gold/Platinum/Enterprise</div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ผู้ใช้
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tier ปัจจุบัน
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  หมดอายุ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  การดำเนินการ
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => {
                const userTierConfig = tierConfigs[user.role]?.[user.tier];
                const isExpired = isTierExpired(user.tierExpiryDate);
                
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.displayName || 'ไม่มีชื่อ'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                          <div className="text-xs text-gray-400">
                            UID: {user.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                        <span className="mr-1">{getRoleIcon(user.role)}</span>
                        {user.role || 'ไม่ระบุ'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const ranking = getTierRanking(user.role, user.tier);
                          return (
                            <>
                              <span className="text-lg">{ranking.icon}</span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${userTierConfig?.color || 'bg-gray-100 text-gray-800'}`}>
                                {userTierConfig?.name || user.tier || 'Free'}
                              </span>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                Rank {ranking.rank}
                              </span>
                            </>
                          );
                        })()}
                        {isExpired && (
                          <span className="ml-2 text-xs text-red-600 font-medium">
                            (หมดอายุ)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.tierExpiryDate ? formatDate(user.tierExpiryDate) : 'ไม่จำกัด'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowTierModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 transition"
                      >
                        เปลี่ยน Tier
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tier Change Modal */}
      {selectedUser && showTierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">เปลี่ยน Tier</h3>
              <button
                onClick={() => setShowTierModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">ผู้ใช้:</label>
                  <p className="font-medium">{selectedUser.displayName || selectedUser.email}</p>
                  <p className="text-sm text-gray-500">UID: {selectedUser.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role:</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(selectedUser.role)}`}>
                    <span className="mr-1">{getRoleIcon(selectedUser.role)}</span>
                    {selectedUser.role || 'ไม่ระบุ'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Tier ปัจจุบัน:</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tierConfigs[selectedUser.role]?.[selectedUser.tier]?.color || 'bg-gray-100 text-gray-800'}`}>
                  {tierConfigs[selectedUser.role]?.[selectedUser.tier]?.name || selectedUser.tier || 'Free'}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  เลือก Tier ใหม่:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {getTierDropdownOptions(tierConfigs, selectedUser.role).map((option) => (
                    <button
                      key={option.tierKey}
                      onClick={() => setNewTier(option.tierKey)}
                      className={`p-3 border rounded-lg text-left transition ${
                        newTier === option.tierKey
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{option.ranking.icon}</span>
                        <div>
                          <div className="font-medium">{option.tierData.name}</div>
                          <div className="text-xs text-gray-500">Rank {option.ranking.rank}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">{option.tierData.price} T Coins/เดือน</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {option.tierData.features.join(', ')}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ระยะเวลา (วัน):
                </label>
                <input
                  type="number"
                  value={tierDuration}
                  onChange={(e) => setTierDuration(parseInt(e.target.value) || 30)}
                  min="1"
                  max="365"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowTierModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={updateUserTier}
                  disabled={!newTier}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                >
                  เปลี่ยน Tier
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TierManagement;
